import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { http } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const usePaymentStatus = (sessionId) => {
  const [status, setStatus] = useState("checking");
  useEffect(() => {
    if (!sessionId) { setStatus("error"); return undefined; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        if (data?.payment_status === "paid") { setStatus("paid"); return; }
        if (data?.status === "expired") { setStatus("expired"); return; }
      } catch (e) {
        if (e?.response?.status === 404) { setStatus("error"); return; }
      }
      tries += 1;
      if (tries >= 10) { setStatus("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);
  return status;
};

const COPY = {
  checking: ["Reading the stars…", "Confirming your payment with Stripe."],
  paid: ["Your bracelet is on its way.", "Thank you for your order. A receipt is coming to your email."],
  expired: ["The session faded.", "Your checkout expired. Nothing was charged."],
  timeout: ["Still confirming…", "Check your email for a receipt, or refresh in a moment."],
  error: ["Something went astray.", "We couldn't find that order."],
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const status = usePaymentStatus(params.get("session_id"));
  const { clear } = useCart();
  useEffect(() => { if (status === "paid") clear(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const [title, sub] = COPY[status];

  return (
    <section className="min-h-screen grid place-items-center px-6 pt-20" data-testid="order-success-container">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="text-center max-w-xl">
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-purple-300" data-testid="payment-status">{status}</p>
        <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">{title}</h1>
        <p className="mt-6 text-purple-100/85">{sub}</p>
        <Link to="/" data-testid="payment-back-home" className="inline-block mt-10 rounded-full bg-purple-200 text-[#0e0616] px-8 py-3.5 font-medium hover:bg-amber-200 transition-colors">
          Back to the hollow
        </Link>
      </motion.div>
    </section>
  );
}

export const PaymentCancel = () => (
  <section className="min-h-screen grid place-items-center px-6 pt-20 text-center" data-testid="payment-cancel-container">
    <div>
      <h1 className="font-display text-5xl md:text-7xl">Checkout paused.</h1>
      <p className="mt-6 text-purple-100/85">Your cart is still waiting for you.</p>
      <Link to="/" data-testid="payment-cancel-back" className="inline-block mt-10 rounded-full border border-purple-300/40 px-8 py-3.5 hover:border-amber-200 hover:text-amber-200 transition-colors">Return</Link>
    </div>
  </section>
);
